import React, { useState, useContext, createContext, useCallback } from 'react';

// Create the context with a null default value
const NotificationContext = createContext(null);

/**
 * A provider component that wraps the app and manages the global error state.
 * @param {object} props - Component props.
 * @param {React.ReactNode} props.children - The child components to render.
 */
export const NotificationProvider = ({ children }) => {
  const [notification, setNotification] = useState({ message: '', open: false });

  // `showNotification` is memoized with useCallback to prevent unnecessary re-renders in consumer components.
  const showNotification = useCallback((message, type) => {
    setNotification({ message, open: true, type });
  }, []);

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setNotification({ ...notification, open: false });
  };

  // The value provided by the context includes the current notification state and the functions to interact with it.
  const contextValue = { notification, showNotification, handleClose };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  );
};

/**
 * Custom hook for easy access to the NotificationContext.
 * This simplifies consuming the context and includes a check to ensure it's used within an NotificationProvider.
 */
export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};
